import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import './FixedNavbar.css';
import ContactPopup from './ContactPopup';

const FixedNavbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState({
    resources: false,
    clientCenter: false
  });
  const [isContactPopupOpen, setIsContactPopupOpen] = useState(false);
  const [showTaxReliefForm, setShowTaxReliefForm] = useState(false);
  const navbarRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // If clicked outside the navbar entirely -> close everything
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        closeAllMenus();
        return;
      }

      // If clicked inside navbar but not inside an open dropdown
      if (openDropdowns.resources && !event.target.closest('[data-menu="resources"]')) {
        setOpenDropdowns(prev => ({ ...prev, resources: false }));
      }
      if (openDropdowns.clientCenter && !event.target.closest('[data-menu="clientCenter"]')) {
        setOpenDropdowns(prev => ({ ...prev, clientCenter: false }));
      }
    };

    // Handle escape key
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeAllMenus();
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openDropdowns]);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
        // Reset mobile dropdowns
        setOpenDropdowns({ resources: false, clientCenter: false });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const toggleMobileMenu = (e) => {
    e.stopPropagation();
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleDropdown = (menu, e) => {
    e.stopPropagation();
    setOpenDropdowns(prev => ({
      resources: menu === 'resources' ? !prev.resources : false,
      clientCenter: menu === 'clientCenter' ? !prev.clientCenter : false
    }));
  };

  const closeAllMenus = () => {
    setIsMobileMenuOpen(false);
    setOpenDropdowns({ resources: false, clientCenter: false });
  };

  // Handle submenu link clicks - prevent event bubbling
  const handleSubmenuClick = (e) => {
    e.stopPropagation();
    // Don't close immediately to allow the click to process
    setTimeout(() => {
      closeAllMenus();
    }, 0);
  };

  // Function to open the contact popup
  const openContactForm = (e) => {
    e.preventDefault();
    setIsContactPopupOpen(true);
    closeAllMenus();
  };

  const closeContactForm = () => {
    setIsContactPopupOpen(false);
  };

  // Function to open Tax Relief Services form
  const openTaxReliefForm = (e) => {
    e.preventDefault();
    setShowTaxReliefForm(true);
    closeAllMenus();
  };

  return (
    <>
      <nav
        className={`fixed-navbar ${isScrolled ? 'scrolled' : ''}`}
        ref={navbarRef}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="nav-container">
          <Link to="/" className="logo">TAX-SMART WEALTH MANAGEMENT</Link>

          {/* Desktop Navigation */}
          <div className="nav-links">
            <NavLink to="/about">About</NavLink>
            <NavLink to="/services">Services</NavLink>

            {/* Resources Dropdown */}
            <div className={`dropdown ${openDropdowns.resources ? 'open' : ''}`} data-menu="resources">
              <div
                className="dropdown-toggle"
                role="button"
                tabIndex="0"
                data-menu="resources"
                aria-expanded={openDropdowns.resources}
                onClick={(e) => toggleDropdown('resources', e)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleDropdown('resources', e);
                  }
                }}
              >
                Resources ▾
              </div>
              <div className="dropdown-menu" role="menu" aria-label="Resources menu">
                <a
                  href="https://taxresources.netlify.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleSubmenuClick}
                >
                  Tax Resources
                </a>
                <a
                  href="https://snowstaxfinancial.blogspot.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleSubmenuClick}
                >
                  Tax Blog & News
                </a>
                <a
                  href="https://simeon-snow-financial-coaching.webflow.io/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleSubmenuClick}
                >
                  Financial Coaching
                </a>
                <a
                  href="https://meetn.com/snowfinancial"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleSubmenuClick}
                >
                  Join Meeting
                </a>
              </div>
            </div>

            {/* Client Center Dropdown */}
            <div className={`dropdown ${openDropdowns.clientCenter ? 'open' : ''}`} data-menu="clientCenter">
              <div
                className="dropdown-toggle"
                role="button"
                tabIndex="0"
                data-menu="clientCenter"
                aria-expanded={openDropdowns.clientCenter}
                onClick={(e) => toggleDropdown('clientCenter', e)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleDropdown('clientCenter', e);
                  }
                }}
              >
                Client Center ▾
              </div>
              <div className="dropdown-menu" role="menu" aria-label="Client Center menu">
                <Link to="/wealth-management" onClick={handleSubmenuClick}>
                  Retirement Account Access
                </Link>
                <a
                  href="https://snowsfinancial.kitchen.co/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleSubmenuClick}
                >
                  Tax Return Portal
                </a>
                <button
                  onClick={openTaxReliefForm}
                  className="dropdown-link-button"
                >
                  Tax Relief Services
                </button>
                <a
                  href="https://snowsfinancial_9db8a.clientbizportal.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleSubmenuClick}
                >
                  1099/W2 Filing Portal
                </a>
                <a
                  href="https://snowsfinancial.kitchen.co/forms/form_7d2831814dcaff3eb7a80801"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleSubmenuClick}
                >
                  Invoice Payment
                </a>
              </div>
            </div>

            <a href="tel:+18702084089" className="phone-btn">(870) 208-4089</a>
          </div>

          {/* Mobile Toggle Button */}
          <button
            className="mobile-toggle"
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
            onClick={toggleMobileMenu}
          >
            ☰
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}
          aria-hidden={!isMobileMenuOpen}
          ref={mobileMenuRef}
        >
          <Link to="/" className="mobile-link" onClick={closeAllMenus}>Home</Link>
          <Link to="/about" className="mobile-link" onClick={closeAllMenus}>About</Link>
          <Link to="/services" className="mobile-link" onClick={closeAllMenus}>Services</Link>

          {/* Mobile Resources Dropdown */}
          <div className={`mobile-dropdown ${openDropdowns.resources ? 'open' : ''}`} data-menu="resources">
            <div
              className="dropdown-toggle"
              role="button"
              tabIndex="0"
              data-menu="resources"
              aria-expanded={openDropdowns.resources}
              onClick={(e) => toggleDropdown('resources', e)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleDropdown('resources', e);
                }
              }}
            >
              Resources ▾
            </div>
            <div className="mobile-dropdown-menu" role="menu" aria-label="Resources mobile menu">
              <a
                href="https://taxresources.netlify.app/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleSubmenuClick}
                className="mobile-submenu-link"
              >
                Tax Resources
              </a>
              <a
                href="https://snowstaxfinancial.blogspot.com/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleSubmenuClick}
                className="mobile-submenu-link"
              >
                Tax Blog & News
              </a>
              <a
                href="https://simeon-snow-financial-coaching.webflow.io/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleSubmenuClick}
                className="mobile-submenu-link"
              >
                Financial Coaching
              </a>
              <a
                href="https://meetn.com/snowfinancial"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleSubmenuClick}
                className="mobile-submenu-link"
              >
                Join Meeting
              </a>
            </div>
          </div>

          {/* Mobile Client Center Dropdown */}
          <div className={`mobile-dropdown ${openDropdowns.clientCenter ? 'open' : ''}`} data-menu="clientCenter">
            <div
              className="dropdown-toggle"
              role="button"
              tabIndex="0"
              data-menu="clientCenter"
              aria-expanded={openDropdowns.clientCenter}
              onClick={(e) => toggleDropdown('clientCenter', e)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleDropdown('clientCenter', e);
                }
              }}
            >
              Client Center ▾
            </div>
            <div className="mobile-dropdown-menu" role="menu" aria-label="Client Center mobile menu">
              <Link to="/wealth-management" onClick={handleSubmenuClick} className="mobile-submenu-link">
                Retirement Account Access
              </Link>
              <a
                href="https://snowsfinancial.kitchen.co/login"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleSubmenuClick}
                className="mobile-submenu-link"
              >
                Tax Return Portal
              </a>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  closeAllMenus();
                  openTaxReliefForm(e);
                }}
                className="mobile-submenu-link text-left w-full"
              >
                Tax Relief Services
              </button>
              <a
                href="https://snowsfinancial_9db8a.clientbizportal.com/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleSubmenuClick}
                className="mobile-submenu-link"
              >
                1099/W2 Filing Portal
              </a>
              <a
                href="https://snowsfinancial.kitchen.co/forms/form_7d2831814dcaff3eb7a80801"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleSubmenuClick}
                className="mobile-submenu-link"
              >
                Invoice Payment
              </a>
            </div>
          </div>

          <a href="tel:+18702084089" className="mobile-link phone-link">
            (870) 208-4089
          </a>
        </div>

        {/* Contact Popup */}
        <ContactPopup isOpen={isContactPopupOpen} onClose={closeContactForm} />
      </nav>

      {/* Tax Relief Services Form Modal */}
      {showTaxReliefForm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-blue-50">
              <div className="flex items-center">
                <img
                  src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1757642747003-community-tax-square-logo.png"
                  alt="Community Tax"
                  className="w-12 h-12 mr-4"
                />
                <h2 className="text-2xl font-bold text-gray-900">Tax Relief Services</h2>
              </div>
              <button onClick={() => setShowTaxReliefForm(false)} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Partnership Information */}
              <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">
                  We partner with one of the largest Tax Resolution Teams in the Country:
                </h3>
                <ul className="text-blue-800 space-y-2">
                  <li>• 4.7 star rating on The Credit Review</li>
                  <li>• Consulted with over 110k clients</li>
                  <li>• Over $1.3 billion in tax liability resolved</li>
                  <li>• Licensed Tax Practitioners, Attorneys, CPA's and IRS Enrolled Agents on Staff</li>
                  <li>• Tax Debt Settlement & Complete Tax Resolution Services</li>
                </ul>
              </div>

              {/* Instructions */}
              <div className="mb-6">
                <p className="text-gray-700 text-center">
                  Input your contact details below & we will reach out to you for a complimentary consultation to see if these services can help you.
                </p>
              </div>

              {/* Simple Contact Form */}
              <form name="community-tax-contact" data-netlify="true" className="space-y-4" method="POST">
                <input type="hidden" name="form-name" value="community-tax-contact" />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name: *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number: *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address: *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount of IRS Debt: *
                  </label>
                  <input
                    type="text"
                    name="irsDebt"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors"
                    placeholder="e.g., $50,000 or Approximate amount"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Describe your situation: *
                  </label>
                  <textarea
                    name="situation"
                    required
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors resize-none"
                    placeholder="Please describe your tax situation..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Best time to contact you: *
                  </label>
                  <input
                    type="text"
                    name="contactTime"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors"
                    placeholder="e.g., Weekdays 9am-5pm"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 transition-colors font-medium"
                >
                  Submit Request
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FixedNavbar;